import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { WritableAtom, useAtom } from 'jotai';
import { z } from 'zod';

import { useDurableFilterRow } from './useDurableFilterRow';

type DurableFilterAtomMirrorOptions<Value> = {
    filtersAtom: WritableAtom<Value, [Value], void>;
    /** The browser storage key the atom is backed by, which also names the stored row. */
    storageKey: string;
    storage: Storage;
    emptyValue: Value;
    schema: z.ZodType<Value>;
    enabled?: boolean;
};

/**
 * Mirrors a storage-backed filter atom into its backend row for as long as the
 * calling page is mounted. Call it once per page: a second call on the same
 * page reads and writes the same row twice over. A page whose filters are not
 * reachable passes `enabled` false, so it neither reads nor writes the row.
 */
export const useDurableFilterAtomMirror = <Value>({
    filtersAtom,
    storageKey,
    storage,
    emptyValue,
    schema,
    enabled = true,
}: DurableFilterAtomMirrorOptions<Value>) => {
    const [value, setValue] = useAtom(filtersAtom);
    const flagEnabled = useFeatureFlagEnabled(FeatureFlag.DASHBOARD_PERSISTED_PAGE_FILTERS);

    useDurableFilterRow({
        storageKey,
        storage,
        value,
        setValue,
        emptyValue,
        schema,
        enabled: enabled && flagEnabled,
    });
};
